import { RefObject } from 'react'
import { FlatList } from 'react-native'
import { IMessage } from 'react-native-gifted-chat'

interface ScrollOptions {
  viewPosition?: number
  animated?: boolean
  maxAttempts?: number
  interval?: number
  fallbackToOffset?: boolean
  onScrollStart?: () => void
  onScrollComplete?: () => void
  onScrollFailed?: () => void
}

export async function scrollToIndexWithRetry(
  listRef: RefObject<FlatList<IMessage> | null>,
  indexToScroll: number,
  onComplete: () => void,
  options?: ScrollOptions
) {
  const {
    viewPosition = 0.5,
    animated = true,
    maxAttempts = 15,
    interval = 150,
    fallbackToOffset = true,
    onScrollStart,
    onScrollComplete,
    onScrollFailed,
  } = options || {}

  const scrollToIndexWithFallback = async (index: number): Promise<boolean> => {
    if (!listRef.current) return false

    try {
      // Primary method: scrollToIndex
      listRef.current.scrollToIndex({
        index,
        viewPosition,
        animated,
      })
      return true
    } catch (error) {
      console.warn('ScrollToIndex failed, attempting fallback methods:', error)

      if (fallbackToOffset) {
        try {
          // Fallback 1: Estimate offset and use scrollToOffset
          const estimatedItemHeight = 70
          const estimatedOffset = index * estimatedItemHeight

          listRef.current.scrollToOffset({
            offset: estimatedOffset,
            animated,
          })
          return true
        } catch (offsetError) {
          console.warn('ScrollToOffset fallback failed:', offsetError)

          try {
            // Fallback 2: Scroll to end then back (for very large indices)
            if (index > 10) {
              listRef.current.scrollToEnd({ animated: false })

              // Wait a bit then try scrollToIndex again
              setTimeout(() => {
                try {
                  listRef.current?.scrollToIndex({
                    index,
                    viewPosition,
                    animated: true,
                  })
                } catch (retryError) {
                  console.warn('Final retry failed:', retryError)
                }
              }, 200)
              return true
            }
          } catch (endScrollError) {
            console.warn('ScrollToEnd fallback failed:', endScrollError)
          }
        }
      }
      return false
    }
  }

  onScrollStart?.()
  let attemptCount = 0

  const attemptScroll = async (): Promise<void> => {
    const success = await scrollToIndexWithFallback(indexToScroll)

    if (success) {
      onScrollComplete?.()
      onComplete()
    } else {
      attemptCount++
      if (attemptCount < maxAttempts) {
        // Retry after interval
        setTimeout(() => {
          attemptScroll().catch(console.error)
        }, interval)
      } else {
        onScrollFailed?.()
        onComplete() // Still call onComplete even if failed
      }
    }
  }

  return attemptScroll()
}
